import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  Filter,
  Upload,
  X,
  Plus,
  User,
  Phone,
  MapPin,
  Mail,
} from "lucide-react";
import { Box } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "../reusableField/button";
import BackButton from "../reusableField/BackButton";
import { RoleContext } from "../reusableField/roleContext";
import "./global.css";
import "./fls.css";
import { useQuery, useMutation } from "@apollo/client";
import {
  CURRENT_USER_PROFIILE,
  GET_FLS_DETAIILS,
  CURRENT_USER_ACCOUNT,
} from "../services/userApi";

const Fls = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId: contextRoleId, setRoleId } = useContext(RoleContext);
  const locationRoleId = location.state?.roleId;

  const roleId = locationRoleId || contextRoleId;

  useEffect(() => {
    if (roleId) {
      setRoleId(roleId);
    }
  }, [roleId, setRoleId]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({});

  //Pagination
  // Add these new states
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(
    location.state?.returnPage || 1
  );

  const limit = 20;

  const { data: userProfileData, loading: userProfileLoading } = useQuery(
    CURRENT_USER_PROFIILE
  );
  const jobRoleId = userProfileData?.currentUserProfile?.roleId;

  // Use roleId from context or jobRoleId as fallback
  const effectiveRoleId = roleId || jobRoleId;

  const {
    data: accountData,
    loading: accountLoading,
    error: accountError,
  } = useQuery(CURRENT_USER_ACCOUNT);
  const accountId = accountData?.currentUserAccount?.id;

  const {
    data: assignedFlsData,
    loading: assignedFlsLoading,
    error: assignedFlsError,
    refetch: assignedFlsRefetch,
  } = useQuery(GET_FLS_DETAIILS, {
    variables: {
      searchTerm: debouncedSearchTerm,
      pagingCriteria: {
        limit: limit,
        page: currentPage,
        sortByField: "name",
        sortOrder: "asc",
      },
      jobroleId: effectiveRoleId,
      // accountId: accountId,
    },
    skip: !effectiveRoleId || !accountId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!assignedFlsLoading && assignedFlsData) {
      console.log("API snapshot:", JSON.parse(JSON.stringify(assignedFlsData)));
    }
  }, [assignedFlsLoading, assignedFlsData]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Update pagination meta
  useEffect(() => {
    if (assignedFlsData?.getOnlyUsersForRoleWithSkipTop?.pageMeta) {
      const {
        totalCount,
        totalPages,
        currentPage: apiCurrentPage,
        hasNextPage,
        hasPreviousPage,
      } = assignedFlsData.getOnlyUsersForRoleWithSkipTop.pageMeta;
      setTotalCount(totalCount);
      setTotalPages(totalPages);
      setCurrentPage(apiCurrentPage);
      setHasNextPage(hasNextPage);
      setHasPreviousPage(hasPreviousPage);
    }
  }, [assignedFlsData]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    if (debouncedSearchTerm !== "") {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);



  const calculateExperience = (startDate) => {
    if (!startDate) return "N/A";

    const start = new Date(startDate);
    const now = new Date();

    let months =
      (now.getFullYear() - start.getFullYear()) * 12 +
      (now.getMonth() - start.getMonth());

    if (now.getDate() < start.getDate()) {
      months -= 1;
    }

    if (months < 0) return "N/A";

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years === 0) {
      return `${remainingMonths} ${remainingMonths === 1 ? "month" : "months"}`;
    }

    if (remainingMonths === 0) {
      return `${years} ${years === 1 ? "year" : "years"}`;
    }

    return `${years} ${years === 1 ? "year" : "years"} ${remainingMonths} ${remainingMonths === 1 ? "month" : "months"
      }`;
  };

  const transformFlsData = (apiData) => {
    if (!apiData?.getOnlyUsersForRoleWithSkipTop?.data) return [];

    return apiData.getOnlyUsersForRoleWithSkipTop.data.map((fls) => {
      return {
        id: fls.id,
        empid: fls.employeeId,
        name: fls.name,
        phone: fls.phoneNumber,
        email: fls.email,
        city: fls.cityName,
        state: fls.stateName,
        experience: calculateExperience(fls.roleStartDate),
      };
    });
  };
  const isLoading = userProfileLoading || assignedFlsLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#dc3545]"></div>
          <p className="ml-3 text-gray-600">Loading</p>
        </div>
      </div>
    );
  }

  // Error state
  if (assignedFlsError) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-600">
            Error loading fls: {assignedFlsError.message}
          </p>
        </div>
      </div>
    );
  }

  const fls = transformFlsData(assignedFlsData);

  // Loading state
  if (assignedFlsLoading) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-600">Loading Users...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (assignedFlsError) {
    return (
      <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[75px] mt-[85px] global">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-600">
            Error loading Fls: {assignedFlsError.message}
          </p>
        </div>
      </div>
    );
  }

  const flsdetails = (flsId) => {
    navigate(`/fls/flsprofile`, {
      state: {
        flsId: flsId,
        jobRoleId: jobRoleId,
        accountId: accountId,
        returnPage: currentPage,
      },
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      handlePageChange(currentPage - 1);
    }
  };

  const PaginationBar = ({
    currentPage,
    totalPages,
    totalCount,
    limit,
    hasPreviousPage,
    hasNextPage,
    handlePreviousPage,
    handleNextPage,
    handlePageChange,
  }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        // mt: 2,
        py: 2,
        px: 2,
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {/* Left: Total Count */}
      <div className="text-sm text-gray-600">
        Showing {(currentPage - 1) * limit + 1} to{" "}
        {Math.min(currentPage * limit, totalCount)} of {totalCount} leads
      </div>

      {/* Center: Page Numbers */}
      <div className="flex items-center gap-2">
        <button
          onClick={handlePreviousPage}
          disabled={!hasPreviousPage}
          className={`px-4 py-2 rounded-md border font-medium transition-colors ${hasPreviousPage
              ? "bg-white border-[#dc3545] text-[#dc3545] hover:bg-[#dc3545] hover:text-white"
              : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
            }`}
        >
          Prev
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {(() => {
            const pages = [];
            const maxVisible = 5;
            let startPage = Math.max(
              1,
              currentPage - Math.floor(maxVisible / 2)
            );
            let endPage = Math.min(totalPages, startPage + maxVisible - 1);

            if (endPage - startPage < maxVisible - 1) {
              startPage = Math.max(1, endPage - maxVisible + 1);
            }

            if (startPage > 1) {
              pages.push(
                <button key={1} onClick={() => handlePageChange(1)}>
                  1
                </button>
              );
              if (startPage > 2) {
                pages.push(<span key="ellipsis1">...</span>);
              }
            }

            for (let i = startPage; i <= endPage; i++) {
              pages.push(
                <button
                  key={i}
                  onClick={() => handlePageChange(i)}
                  className={`px-3 py-2 rounded-md border font-medium transition-colors ${currentPage === i
                      ? "bg-[#dc3545] text-white border-[#dc3545]"
                      : "border-gray-300 hover:border-[#dc3545] hover:bg-gray-50"
                    }`}
                >
                  {i}
                </button>
              );
            }

            if (endPage < totalPages) {
              if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis2">...</span>);
              }
              pages.push(
                <button
                  key={totalPages}
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              );
            }

            return pages;
          })()}
        </div>

        <button
          onClick={handleNextPage}
          disabled={!hasNextPage}
          className={`px-4 py-2 rounded-md border font-medium transition-colors ${hasNextPage
              ? "bg-white border-[#dc3545] text-[#dc3545] hover:bg-[#dc3545] hover:text-white"
              : "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
            }`}
        >
          Next
        </button>
      </div>

      {/* Right: Page Info */}
      <div className="text-sm text-gray-600">
        Page {currentPage} of {totalPages}
      </div>
    </Box>
  );

  return (
    <div className="min-h-screen bg-[#f4f4f4] p-4 md:p-6 ml-[15px] mt-[85px] global">
      {/* Header */}
      {console.log("Role ID:", jobRoleId)}
      <div className="mb-6">
        <div style={{ marginBottom: "16px" }}>
          <BackButton label="Back" />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search name/ email...."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-[#888888] rounded-[4px] focus:outline-none focus:border-[#dc3545] text-[14px]"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          </div>
        </div>

        <h2 className="text-xl font-semibold text-gray-800">User Details</h2>
      </div>

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={limit}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        handlePageChange={handlePageChange}
      />

      {/* Leads List */}
      <div className="space-y-4">
        {!assignedFlsLoading && fls.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No Users found.</p>
          </div>
        ) : (
          fls.map((lead, index) => (
            <div
              key={lead.id}
              className="bg-white rounded-lg border border-[#888888] rounded-[4px] p-4 md:p-6"
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                    <h3
                      className="text-lg font-semibold text-[#1F2020]-600 cursor-pointer hover:text-blue-600"
                      onClick={() => flsdetails(lead.id)}
                    >
                      {lead.name || "N/A"}
                    </h3>
                    <span className="text-gray-600 font-medium">
                      # {lead.empid}
                    </span>
                  </div>

                  <div className="space-y-2 text-gray-600">
                    <div className="flex items-start gap-2">
                      <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{lead.phone || "N/A"}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{lead.email || "N/A"}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {lead.city}, {lead.state}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex-shrink-0">
                  <span
                    className="exp"
                  // className={`px-3 py-1 rounded-full text-sm font-medium`}
                  >
                    Exp : <span className="years">{lead.experience}</span>
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        limit={limit}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        handlePageChange={handlePageChange}
      />
    </div>
  );
};

export default Fls;
